import {QuestionsAPI} from 'api/questions';
import {TrackingAPI} from 'api/tracking';
import {AsyncButton} from 'components/system/async_button';
import {Button} from 'components/system/button';
import {Divider} from 'components/system/divider';
import {Latex} from 'components/system/latex';
import {Sticky} from 'components/system/sticky';
import {useTabs} from 'components/system/tabs';
import {TextContainer} from 'components/system/text_container';
import {useIsMobile} from 'hooks/is_mobile';
import {useEffect, useRef, useState} from 'react';
import {useUpsell} from 'users';
import {RecommendedQuestions} from '../recommended_questions';
import styles from './question_body.module.css';

export const QuestionBody = ({question, showRecommendedQuestions = true}: Props) => {
  const {permissionRequired} = useUpsell();
  const {setTab} = useTabs();
  const isMobile = useIsMobile();
  const [hints, setHints] = useState<string[]>([]);
  const [gotchas, setGotchas] = useState<string[]>([]);
  const [visibleHints, setVisibleHints] = useState(0);
  const [visibleGotchas, setVisibleGotchas] = useState(0);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const lastHintRef = useRef<HTMLDivElement>(null);
  const lastGotchaRef = useRef<HTMLDivElement>(null);

  const scrollIntoView = (e: HTMLDivElement) => {
    window.scrollTo({
      top: e.getBoundingClientRect().top + window.scrollY - buttonsRef.current!.offsetHeight + 40,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (visibleHints > 0 && lastHintRef.current) {
      scrollIntoView(lastHintRef.current);
    }
  }, [visibleHints]);

  useEffect(() => {
    if (visibleGotchas > 0 && lastGotchaRef.current) {
      scrollIntoView(lastGotchaRef.current);
    }
  }, [visibleGotchas]);

  // Reset everything if question changes.
  useEffect(() => {
    setHints([]);
    setGotchas([]);
    setVisibleHints(0);
    setVisibleGotchas(0);
  }, [question]);

  return (
    <div>
      <TextContainer>
        <div className={styles.questionBody} data-question-section='body'>
          <Latex>{question.body}</Latex>
        </div>
        <Sticky shadow threshold={20}>
          <div className={styles.buttons} ref={buttonsRef}>
            {question.has_hints && (hints.length === 0 || visibleHints < hints.length) && (
              <AsyncButton
                display={isMobile ? 'block' : 'inline'}
                type='outline'
                allowClick={() =>
                  permissionRequired('question_hint_or_gotcha_view', question.uuid, {
                    sourceObject: question,
                    cta: 'question.viewHintsOrGotchas',
                  })()
                }
                onRequest={() => QuestionsAPI.getHintsAndGotchas(question.slug)}
                onSuccess={({hints, gotchas}) => {
                  setHints(hints);
                  setGotchas(gotchas);
                  setVisibleHints(1);
                  TrackingAPI.hintOrGotchaViewed(question);
                }}
                onClick={
                  hints.length > 0
                    ? () => {
                        setVisibleHints((visibleHints) => visibleHints + 1);
                        TrackingAPI.hintOrGotchaViewed(question);
                      }
                    : undefined
                }>
                I need help
              </AsyncButton>
            )}
            {question.has_gotchas && (gotchas.length === 0 || visibleGotchas < gotchas.length) && (
              <AsyncButton
                display={isMobile ? 'block' : 'inline'}
                type='outline'
                allowClick={() =>
                  permissionRequired('question_hint_or_gotcha_view', question.uuid, {
                    sourceObject: question,
                    cta: 'question.viewHintsOrGotchas',
                  })()
                }
                onRequest={() => QuestionsAPI.getHintsAndGotchas(question.slug)}
                onSuccess={({hints, gotchas}) => {
                  setHints(hints);
                  setGotchas(gotchas);
                  setVisibleGotchas(1);
                  TrackingAPI.hintOrGotchaViewed(question);
                }}
                onClick={
                  gotchas.length > 0
                    ? () => {
                        setVisibleGotchas((visibleGotchas) => visibleGotchas + 1);
                        TrackingAPI.hintOrGotchaViewed(question);
                      }
                    : undefined
                }>
                I've got an answer
              </AsyncButton>
            )}
            <Button
              color='orange'
              display={isMobile ? 'block' : 'inline'}
              onClick={() => setTab('Solution')}>
              Show solution
            </Button>
          </div>
        </Sticky>
        {hints.slice(0, visibleHints).map((hint, index) => (
          <div key={index} data-question-section='hint'>
            <div
              ref={index === visibleHints - 1 ? lastHintRef : undefined}
              className={styles.ref}
            />
            <Divider>
              <strong>Hint #{index + 1}</strong>
            </Divider>
            <Latex>{hint}</Latex>
          </div>
        ))}
        {gotchas.slice(0, visibleGotchas).map((gotcha, index) => (
          <div key={index} data-question-section='gotcha'>
            <div
              ref={index === visibleGotchas - 1 ? lastGotchaRef : undefined}
              className={styles.ref}
            />
            <Divider>
              <strong>Gotcha #{index + 1}</strong>
            </Divider>
            <Latex>{gotcha}</Latex>
          </div>
        ))}
      </TextContainer>
      {showRecommendedQuestions && question.recommended_questions.length > 0 && (
        <>
          <Divider />
          <RecommendedQuestions
            questions={question.recommended_questions}
            collapsible={true}
            initialCollapsed={true}
            trackingComponent='questionDetail'
          />
        </>
      )}
    </div>
  );
};

type Props = {
  question: Question;
  showRecommendedQuestions?: boolean;
};
